import React from 'react';
import {
  Card,
  CardHeader,
  CardContent,
  Grid,
  Box,
  Typography,
  Chip,
  Divider,
} from '@mui/material';
import {
  Assessment as AssessmentIcon,
  Storage as StorageIcon,
  People as PeopleIcon,
  Code as CodeIcon,
  Extension as ExtensionIcon,
  Forum as ForumIcon,
  Message as MessageIcon,
  TrendingUp as TrendingUpIcon,
} from '@mui/icons-material';
import { ObservatoryStatistics, ObservatorySnapshot } from './hooks/useAIObservatory';

interface StatisticsPanelProps {
  statistics: ObservatoryStatistics | null;
  snapshot: ObservatorySnapshot | null;
}

export const StatisticsPanel: React.FC<StatisticsPanelProps> = ({ statistics, snapshot }) => {
  if (!statistics) {
    return (
      <Card>
        <CardHeader title={<><AssessmentIcon /> Statistics</>} />
        <CardContent>
          <Typography>Loading statistics...</Typography>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader title="System Statistics" />
      <CardContent>
        {/* Core Metrics */}
        <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600, color: 'text.secondary' }}>
          Core Metrics
        </Typography>
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={6} md={3}>
            <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
              <StorageIcon sx={{ fontSize: 36, color: 'primary.main', mb: 1 }} />
              <Typography variant="h4" sx={{ fontWeight: 'bold' }}>{statistics.totalConfigurations}</Typography>
              <Typography variant="caption" color="text.secondary">CONFIGURATIONS</Typography>
            </Box>
          </Grid>

          <Grid item xs={6} md={3}>
            <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
              <PeopleIcon sx={{ fontSize: 36, color: 'secondary.main', mb: 1 }} />
              <Typography variant="h4" sx={{ fontWeight: 'bold' }}>{statistics.totalInstances}</Typography>
              <Typography variant="caption" color="text.secondary">INSTANCES</Typography>
            </Box>
          </Grid>

          <Grid item xs={6} md={3}>
            <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
              <PeopleIcon sx={{ fontSize: 36, color: 'info.main', mb: 1 }} />
              <Typography variant="h4" sx={{ fontWeight: 'bold' }}>{statistics.totalUsers}</Typography>
              <Typography variant="caption" color="text.secondary">USERS</Typography>
            </Box>
          </Grid>

          <Grid item xs={6} md={3}>
            <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
              <CodeIcon sx={{ fontSize: 36, color: 'success.main', mb: 1 }} />
              <Typography variant="h4" sx={{ fontWeight: 'bold' }}>{statistics.functionCounts.totalFunctions}</Typography>
              <Typography variant="caption" color="text.secondary">FUNCTIONS</Typography>
              <Typography variant="caption" sx={{ display: 'block', mt: 0.5, fontSize: '0.65rem' }}>
                From Feature Providers
              </Typography>
            </Box>
          </Grid>
        </Grid>

        <Divider sx={{ my: 3 }} />

        {/* Conversation Metrics */}
        <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600, color: 'text.secondary' }}>
          Conversation Metrics
        </Typography>
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={6} md={3}>
            <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
              <ForumIcon sx={{ fontSize: 36, color: 'primary.main', mb: 1 }} />
              <Typography variant="h4" sx={{ fontWeight: 'bold' }}>{statistics.totalThreads}</Typography>
              <Typography variant="caption" color="text.secondary">TOTAL THREADS</Typography>
              <Chip 
                label={`${statistics.activeThreads} active`} 
                size="small" 
                color="success" 
                sx={{ mt: 0.5 }}
              />
            </Box>
          </Grid>

          <Grid item xs={6} md={3}>
            <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
              <MessageIcon sx={{ fontSize: 36, color: 'secondary.main', mb: 1 }} />
              <Typography variant="h4" sx={{ fontWeight: 'bold' }}>{statistics.totalMessages}</Typography>
              <Typography variant="caption" color="text.secondary">TOTAL MESSAGES</Typography>
              <Chip 
                label={`${statistics.conversationStats.averageMessagesPerThread} avg/thread`} 
                size="small" 
                variant="outlined"
                sx={{ mt: 0.5 }}
              />
            </Box>
          </Grid>

          <Grid item xs={6} md={3}>
            <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
              <TrendingUpIcon sx={{ fontSize: 36, color: 'success.main', mb: 1 }} />
              <Typography variant="h4" sx={{ fontWeight: 'bold' }}>{statistics.conversationStats.threadsToday}</Typography>
              <Typography variant="caption" color="text.secondary">THREADS TODAY</Typography>
            </Box>
          </Grid>

          <Grid item xs={6} md={3}>
            <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
              <TrendingUpIcon sx={{ fontSize: 36, color: 'info.main', mb: 1 }} />
              <Typography variant="h4" sx={{ fontWeight: 'bold' }}>{statistics.conversationStats.messagesToday}</Typography>
              <Typography variant="caption" color="text.secondary">MESSAGES TODAY</Typography>
            </Box>
          </Grid>
        </Grid>

        <Divider sx={{ my: 3 }} />

        {/* Provider Distribution */}
        <Box sx={{ mt: 3 }}>
          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600, color: 'text.secondary' }}>
            Provider Distribution
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <Chip
              icon={<StorageIcon />}
              label={`Tenant Types: ${statistics.providerCounts.tenantTypeProviders}`}
              color="primary"
              variant="outlined"
            />
            <Chip
              icon={<ExtensionIcon />}
              label={`Applications: ${statistics.providerCounts.applicationProviders}`}
              color="secondary"
              variant="outlined"
            />
            <Chip
              icon={<ExtensionIcon />}
              label={`Features: ${statistics.providerCounts.featureProviders}`}
              color="info"
              variant="outlined"
            />
          </Box>
        </Box>

        {/* Data Flow Visualization */}
        <Box sx={{ mt: 3, p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600, textAlign: 'center' }}>
            Data Flow
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2, flexWrap: 'wrap' }}>
            <Box sx={{ textAlign: 'center' }}>
              <Chip label={`${statistics.totalConfigurations} Configs`} color="primary" />
              <Typography variant="caption" sx={{ display: 'block', mt: 0.5 }}>
                Agent Definitions
              </Typography>
            </Box>
            <Typography variant="h6" color="text.secondary">→</Typography>
            <Box sx={{ textAlign: 'center' }}>
              <Chip label={`${statistics.totalInstances} Instances`} color="secondary" />
              <Typography variant="caption" sx={{ display: 'block', mt: 0.5 }}>
                Per-User Agents
              </Typography>
            </Box>
            <Typography variant="h6" color="text.secondary">→</Typography>
            <Box sx={{ textAlign: 'center' }}>
              <Chip label={`${statistics.totalThreads} Threads`} color="info" />
              <Typography variant="caption" sx={{ display: 'block', mt: 0.5 }}>
                Conversations
              </Typography>
            </Box>
            <Typography variant="h6" color="text.secondary">→</Typography>
            <Box sx={{ textAlign: 'center' }}>
              <Chip label={`${statistics.totalMessages} Messages`} color="success" />
              <Typography variant="caption" sx={{ display: 'block', mt: 0.5 }}>
                Exchanges
              </Typography>
            </Box>
          </Box>
        </Box>

        {snapshot && (
          <Box sx={{ mt: 3, pt: 3, borderTop: 1, borderColor: 'divider' }}>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>Real-time Activity</Typography>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
              <Chip label={`Active Sessions: ${snapshot.activeSessions.length}`} color="success" variant="outlined" />
              <Chip label={`Active Threads: ${snapshot.activeThreads.length}`} color="warning" variant="outlined" />
              <Chip label={`Connected Clients: ${snapshot.connectedClients}`} variant="outlined" />
            </Box>

            {Object.keys(snapshot.functionMetrics).length > 0 && (
              <Box>
                <Typography variant="subtitle2" sx={{ mb: 1, fontSize: '0.875rem', color: 'text.secondary' }}>Top Functions</Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  {Object.entries(snapshot.functionMetrics)
                    .sort((a, b) => b[1].count - a[1].count)
                    .slice(0, 5)
                    .map(([funcName, metrics]) => (
                      <Box
                        key={funcName}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                          p: 1,
                          bgcolor: 'background.default',
                          borderRadius: 1,
                        }}
                      >
                        <Typography
                          sx={{
                            flex: 1,
                            fontFamily: 'monospace',
                            fontSize: '0.875rem',
                          }}
                        >
                          {funcName}()
                        </Typography>
                        <Chip label={`${metrics.count} calls`} color="primary" size="small" />
                        <Typography variant="caption" color="text.secondary">
                          {Math.round(metrics.totalMs / metrics.count)}ms avg
                        </Typography>
                      </Box>
                    ))}
                </Box>
              </Box>
            )}
          </Box>
        )}
      </CardContent>
    </Card>
  );
};
